import React, { ReactNode, useEffect, useReducer, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { pick, values } from 'lodash';
import { ReactComponent as GroupIcon } from 'new/components/icons/group-points.svg';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import SearchBar from 'js/components/MacroInput/SearchBar';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import SearchModalItem from './SearchModalItem';
import './SearchModal.scss';
import { AvailableFilterTypesByView } from 'js/constants/visibilityFilters';
import {
  deselectCard,
  resetPageNumbers,
  selectCard,
  setContextualSupport,
  unsetContextualSupport,
} from 'js/actions';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import Constants from 'js/Constants';
import { Stack } from 'new/elements/Stack';
import {
  getCurrentSelectedOption,
  listSelectStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import { AvailableFilters } from 'js/components/MacroInput/types/AvailableFilters';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import { SUPPORT_KEY_REUSABLE_TX } from 'js/constants/supportPageKeys';
import SearchIcon from 'js/components/elements/icons/SearchIcon';
import {
  ReusableTransformDetailHeader,
  useGetDataToReusableTransformDetailHeader,
} from 'js/components/reusableTransforms/cards/ReusableTransformDetailHeader';
import { ReusableTransformType } from '@nexla/@types';
import StyledFixedPanel from 'new/components/StyledFixedPanel';
import CardDetailView from 'js/components/elements/cards/CardDetailView';
import { ReusableTransformUtil } from 'new/utils/ReusableTransform/argumentValues';
import { Button } from '../Button';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ThunkAction } from 'redux-thunk';
import { Action } from 'redux';
import { useLocalAccessLevel } from 'new/hooks/useLocalAccessLevel';
import { useHandleAccessLevelChanges } from 'new/hooks/useHandleAccessLevelChanges';

type Option = {
  label: string;
  id: string | number;
  description: string;
  value?: any;
};

type SearchModalProps = {
  title?: string;
  children?: ReactNode;
  data?: Array<any>;
  view: string;
  isLoading: boolean;
  buttonLabel: string;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  inModal?: boolean;
  onConfirmButtonClick(item: any): void;
  loadData?: (
    page: number,
    sortCriteria: string,
    availableFilters: AvailableFilters,
    setGlobalPagination?: boolean,
    options?: { aclQueryMode?: string },
  ) => ThunkAction<unknown, any, any, Action<any>>;
  onClickPreviewData?(id: number): Promise<string>;
};

const modalListingDefaults = {
  currentPage: 1,
  totalPages: null,
  totalItems: null,
  sortCriteria: 'NAME_ASC',
};

function reducer(state, action) {
  switch (action.type) {
    case 'updateCurrentPage':
      return { ...state, currentPage: action.value };
    case 'updateTotalPages':
      return { ...state, totalPages: action.value };
    case 'updateTotalItems':
      return { ...state, totalItems: action.value };
    case 'updateSortCriteria':
      return { ...state, sortCriteria: action.value };
  }
  throw Error('Unknown action: ' + action.type);
}

export const SearchModal = (props: SearchModalProps) => {
  const {
    title,
    data,
    view,
    isLoading,
    buttonLabel,
    cancelButtonLabel,
    confirmButtonLabel,
    inModal = true,
    onConfirmButtonClick,
    loadData,
  } = props;

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option | null>(null);
  const [resource, setResource] = useState(null);
  const { filters: allFilters, availableFilters: allAvailableFilters } = useSelector(
    (state: any) => state.app.visibilityFilters,
  );
  const [state, listingDispatch] = useReducer(reducer, modalListingDefaults);
  const dispatch = useAppDispatch();
  const prevFilters = useRef();
  const filters = allFilters[view];
  const availableFilterTypes = AvailableFilterTypesByView[view];
  const sortCriteriaOptions = values(Constants.SortCriteriaInfo);
  const selectedSortOption = getCurrentSelectedOption(sortCriteriaOptions, state.sortCriteria);

  const { aclQueryMode: localAclQueryMode, ...accessLevelControlProps } = useLocalAccessLevel();

  const toggle = () => setOpen(!open);

  const getDataCallback = useGetDataToReusableTransformDetailHeader(data, localAclQueryMode);

  const loadListData = () => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);

    if (loadData) {
      dispatch(
        loadData(state.currentPage, state.sortCriteria, availableFilters, false, {
          aclQueryMode: localAclQueryMode,
        }),
        // @ts-ignore
      ).then((response) => {
        listingDispatch({
          type: 'updateTotalPages',
          value: response.totalPages,
        });
        listingDispatch({
          type: 'updateTotalItems',
          value: response.totalItems,
        });
      });
    }
  };

  useHandleAccessLevelChanges(loadListData, [localAclQueryMode]);

  useEffect(() => {
    dispatch(resetPageNumbers());
    dispatch(setContextualSupport(SUPPORT_KEY_REUSABLE_TX));
    loadListData();

    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_REUSABLE_TX));
    };
  }, []);

  useEffect(
    function detectFiltersChange() {
      if (filters && prevFilters.current && areSegmentsDifferent(filters, prevFilters.current)) {
        loadListData();
      }

      prevFilters.current = filters;
    },
    [filters],
  );

  const onChangeListingItem = ({ type, value }) => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);
    const { currentPage, sortCriteria } = state;
    listingDispatch({ type: type, value: value });

    if (type === 'updateCurrentPage') {
      dispatch(loadData(value, sortCriteria, availableFilters, false));
    } else if (type === 'updateSortCriteria') {
      dispatch(loadData(currentPage, value, availableFilters, false));
    }
  };

  const onSortCriteriaChanged = async (selectedOption) => {
    onChangeListingItem({
      type: 'updateSortCriteria',
      value: selectedOption.value,
    });
  };

  const onConfirmClicked = () => {
    if (selected && typeof onConfirmButtonClick === 'function') {
      onConfirmButtonClick(selected);
      setOpen(false);
    }
  };

  const onCloseSelectedCard = async () => {
    await dispatch(deselectCard());
    setResource(null);
  };

  const onOpenSelectedCard = async (card: ReusableTransformType) => {
    await dispatch(selectCard(card.id));
    setResource({
      data: card,
      tabs: ReusableTransformUtil.generateTabs(data),
      detailHeaderContent: React.createElement(ReusableTransformDetailHeader, {
        ...getDataCallback(data.findIndex((d) => d.id === card.id)),
        adminAccess: false,
        hideActions: false,
        showActionsContainer: false,
        onCloseDrawer: onCloseSelectedCard,
      }),
    });
  };

  const ButtonLayout = (buttonLabel: string) => {
    switch (buttonLabel) {
      case 'Search':
        return (
          <span className="search">
            <SearchIcon />
            {buttonLabel}
          </span>
        );
      default:
        return (
          <span className="sample">
            {buttonLabel}
            <GroupIcon width={16} height={16} />
          </span>
        );
    }
  };

  const onSelectSearchModalItem = (item: any) => setSelected(item);

  return (
    <div className="search-root-container">
      <Button name="search-btn" onClick={toggle}>
        {ButtonLayout(buttonLabel)}
      </Button>
      {open ? (
        <div className="search-modal-container">
          <div className="modal-fader" onClick={toggle} />
          <div className="search-modal-content">
            <div className="search-modal-header">
              <h1 className="title">{title}</h1>
              <div className="icon" />
            </div>
            <div className="search-modal-search-bar">
              <MacroOptionsContextWrap>
                <SearchBar
                  view={view}
                  availableFilterTypes={availableFilterTypes}
                  inModal={inModal}
                  accessLevelControlProps={{
                    controlled: inModal,
                    ...accessLevelControlProps,
                  }}
                />
              </MacroOptionsContextWrap>
              <UnsortedSelect
                options={sortCriteriaOptions}
                components={{ Input: SelectInput, Option: SelectOption }}
                isClearable={false}
                isSearchable={true}
                backspaceRemovesValue={false}
                value={selectedSortOption}
                onChange={onSortCriteriaChanged}
                styles={listSelectStyles}
              />
              <CardsListPageNumbersContainer
                isLoading={isLoading}
                listing={state}
                onListingChange={onChangeListingItem}
              />
            </div>
            <div className="search-modal-body">
              <ul className="search-modal-list thin-scroll-bar-style">
                {!isLoading && (!data || !data.length) && (
                  <NoContentTextView title={<div>No results match your search criteria.</div>} />
                )}
                {data?.map((item, idx) => {
                  const checked = item?.id === selected?.id;
                  return (
                    <SearchModalItem
                      key={idx}
                      itemData={item}
                      checked={checked}
                      onSelect={onSelectSearchModalItem}
                      onClickCard={onOpenSelectedCard}
                      onClickPreviewData={props?.onClickPreviewData}
                    />
                  );
                })}
              </ul>
            </div>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              padding="20px"
            >
              <Button variant="outlined" onClick={toggle}>
                {cancelButtonLabel}
              </Button>
              <Button variant="contained" disabled={!selected} onClick={onConfirmClicked}>
                {confirmButtonLabel}
              </Button>
            </Stack>
          </div>
        </div>
      ) : null}
      {resource ? (
        <div className="attribute-transform-overlay-modal">
          <div className="modal-fader" onClick={onCloseSelectedCard} />
          <StyledFixedPanel>
            <CardDetailView
              tabs={[]}
              noHeaderPadding
              aclQueryMode={localAclQueryMode}
              {...resource}
            />
          </StyledFixedPanel>
        </div>
      ) : null}
    </div>
  );
};
